import { timeBetween } from "@app/date";
import { fetchApps, selectLatestAppDeployment } from "@app/deploy";
import {
  fetchDeploymentById,
  fetchDeployments,
  selectDeploymentById,
} from "@app/deployment";
import { selectOrganizationSelected } from "@app/organizations";
import { useCompositeLoader, useQuery, useSelector } from "@app/react";
import {
  appDetailDeploymentsUrl,
  envSelectUrl,
  sourceDetailUrl,
  sourcesSetupUrl,
} from "@app/routes";
import type { WebState } from "@app/schema";
import { fetchSourceById, fetchSources, selectSourceById } from "@app/source";
import type { DeploySource, Deployment } from "@app/types";
import {
  GitRef,
  IconCheckCircle,
  IconXCircle,
  SourceLogo,
  Tooltip,
} from "@app/ui/shared";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { LastDeployLoader } from "./loading";
import {
  HOME_DASH_EVENTS,
  homeDashEvent,
  homeDashOrgPayload,
} from "./tuna-events";

interface HomePageSourceInfoProps {
  source?: DeploySource | null;
  deployment?: Deployment | null;
  isLoading?: boolean;
  org: { id: string; name: string };
}

const HomePageSourceInfo = ({
  source,
  deployment,
  isLoading = false,
  org,
}: HomePageSourceInfoProps) => {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">Loading...</span>
      </div>
    );
  }

  if (!source || !deployment) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">
          Commit data not available.{" "}
        </span>
        <Link
          to={sourcesSetupUrl()}
          className="text-sm font-medium text-blue-600 hover:text-blue-800"
          onClick={() =>
            homeDashEvent(HOME_DASH_EVENTS.LAST_DEPLOY_CONNECT_SOURCE_CTA, {
              ...homeDashOrgPayload(org),
            })
          }
        >
          Connect Source →
        </Link>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <SourceLogo source={source} className="w-6 h-6" />
      <Link
        to={sourceDetailUrl(source.id)}
        className="text-sm font-medium text-gray-900 hover:text-blue-600"
        onClick={() =>
          homeDashEvent(HOME_DASH_EVENTS.LAST_DEPLOY_SOURCE_LINK_CLICK, {
            sourceId: source.id,
            ...homeDashOrgPayload(org),
          })
        }
      >
        {source.displayName}
      </Link>
      <span className="text-sm text-gray-500">|</span>
      <GitRef
        gitRef={deployment.gitRef}
        commitSha={deployment.gitCommitSha}
        commitUrl={deployment.gitCommitUrl}
      />
    </div>
  );
};

export const LastDeploy = () => {
  const org = useSelector(selectOrganizationSelected);
  const loader = useCompositeLoader([
    fetchApps,
    fetchDeployments,
    fetchSources,
  ]);
  const latestAppWithDeployment = useSelector((s: WebState) =>
    selectLatestAppDeployment(s),
  );

  const deploymentId = latestAppWithDeployment?.deployment?.id;
  const sourceId = latestAppWithDeployment?.app?.currentSourceId;

  // Fetch the specific source if we have a sourceId
  const sourceFetchAction = useMemo(
    () => fetchSourceById({ id: sourceId || "" }),
    [sourceId],
  );
  useQuery(sourceFetchAction);

  const latestSource = useSelector((state: WebState) =>
    sourceId ? selectSourceById(state, { id: sourceId }) : null,
  );

  // Fetch deployment details if we have a deploymentId
  const deploymentFetchAction = useMemo(
    () => fetchDeploymentById({ id: deploymentId || "" }),
    [deploymentId],
  );
  useQuery(deploymentFetchAction);

  // Get deployment from store (prefer detailed deployment over list deployment)
  const deployment = useSelector((state: WebState) =>
    deploymentId
      ? selectDeploymentById(state, { id: deploymentId }) ||
        latestAppWithDeployment?.deployment
      : latestAppWithDeployment?.deployment,
  );

  // Show loading for initial data load
  const isLoading = loader.isInitialLoading;

  // Check if we're still waiting for data to load
  // This includes initial loading OR waiting for specific data when we have IDs
  const isWaitingForData = Boolean(
    loader.isLoading ||
      loader.isInitialLoading ||
      (sourceId && !latestSource) ||
      (deploymentId && !deployment),
  );

  // Calculate relative time
  const relativeTime = useMemo(() => {
    if (!deployment) return null;
    // Swap dates to get "ago" format - timeBetween uses end.toRelative({ base: start })
    // So we pass now as start and deployment time as end to get "X ago" format
    return timeBetween({
      startDate: new Date().toISOString(),
      endDate: deployment.createdAt,
    });
  }, [deployment]);

  if (isLoading) {
    return <LastDeployLoader />;
  }

  return (
    <div className="flex flex-col h-full">
      <h2 className="text-lg font-medium mb-4">Last Deploy</h2>
      <div className="bg-white rounded-lg border border-gray-200 shadow flex-1">
        <div className="p-4">
          {!latestAppWithDeployment ? (
            <div className="flex min-h-[80px] items-center justify-center">
              <div className="flex flex-col items-center gap-1 text-sm text-gray-500 text-center">
                <span>No recent deployments found.</span>
                <Link
                  to={envSelectUrl()}
                  className="font-medium text-blue-600 hover:text-blue-800"
                  onClick={() =>
                    homeDashEvent(
                      HOME_DASH_EVENTS.LAST_DEPLOY_EMPTY_DEPLOY_CTA,
                      {
                        ...homeDashOrgPayload(org),
                      },
                    )
                  }
                >
                  Deploy an App →
                </Link>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {/* First line: App name with icon, status, and timestamp (right-aligned) */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <img
                    src="/resource-types/logo-app.png"
                    className="w-6 h-6"
                    aria-label="App"
                  />
                  <Link
                    to={appDetailDeploymentsUrl(latestAppWithDeployment.app.id)}
                    className="text-sm font-medium text-gray-900 hover:text-blue-600"
                    onClick={() =>
                      homeDashEvent(
                        HOME_DASH_EVENTS.LAST_DEPLOY_APP_LINK_CLICK,
                        {
                          appId: latestAppWithDeployment.app.id,
                          ...homeDashOrgPayload(org),
                        },
                      )
                    }
                  >
                    {latestAppWithDeployment.app.handle}
                  </Link>
                </div>
                <div className="flex items-center gap-2">
                  {deployment && (
                    <>
                      {deployment.status === "succeeded" && (
                        <Tooltip
                          text="Deploy successful"
                          placement="bottom"
                          fluid
                        >
                          <IconCheckCircle color="#00633F" variant="sm" />
                        </Tooltip>
                      )}
                      {deployment.status === "failed" && (
                        <Tooltip text="Deploy failed" placement="bottom" fluid>
                          <IconXCircle color="#AD1A1A" variant="sm" />
                        </Tooltip>
                      )}
                    </>
                  )}
                  {relativeTime && (
                    <span className="text-sm text-gray-600">
                      {relativeTime}
                    </span>
                  )}
                </div>
              </div>

              {/* Divider between rows */}
              <div className="h-px bg-gray-200 my-2" />

              {/* Second line: Source, Git Ref */}
              <HomePageSourceInfo
                source={latestSource}
                deployment={deployment}
                isLoading={isWaitingForData}
                org={org}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
